import { PatchScript } from '@pos-refacto/patchscript-with-react';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import * as R from 'ramda';

import i18next from 'containers/App/i18n';
import { createConfirmation } from 'actions/Confirmation';
import { getCafaEntry2 } from 'reducers/cafaConfigs';
import { INTEGRATION_TYPES, INTEGRATIONS } from 'constants/CAFA';
import { printPatchscript } from 'actions/integrations/printer';
import { ValueOf } from 'utils';
import { RootState } from 'reducers';

import { PaymentResponse } from '../requests/types';
import * as CONSTANTS from '../constants';

function generatePatchScript(rawReceipt: string[]): PatchScript {
  return rawReceipt.map(text => ({
    type: 'text',
    align: 'center',
    pieces: [{ text }],
  }));
}

export function getPatchScriptReceipt(response: PaymentResponse) {
  const receiptData = response?.additionalData?.receipt;
  // Receipt might be missing because MS did not return it or installed MS version does not support returning receipts
  if (!receiptData || !(receiptData instanceof Array) || !receiptData.length) {
    return {};
  }
  const patchScriptReceipt = generatePatchScript(receiptData);
  // MS returns only one receipt so use it for both card holder and merchant
  return {
    cardholderPatchScript: patchScriptReceipt,
    merchantPatchScript: patchScriptReceipt,
  };
}

export function convertReceiptsToCopies(
  receipts: ReturnType<typeof getPatchScriptReceipt>,
): ReturnType<typeof getPatchScriptReceipt> {
  const copyHeader: PatchScript[number] = {
    type: 'text',
    align: 'center',
    pieces: [{ text: 'COPY' }],
  };
  return R.evolve(
    {
      cardholderPatchScript: R.prepend(copyHeader),
      merchantPatchScript: R.prepend(copyHeader),
    },
    receipts,
  );
}

function confirmCopyPrint(
  receiptRecipient: ValueOf<typeof CONSTANTS.RECEIPT_RECIPIENTS>,
) {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action>) => {
    const translatedReceiptRecipient = i18next.t(
      `paymentIntegrations:tripos.printReceipt.${receiptRecipient}`,
    );
    return new Promise<boolean>(resolve =>
      dispatch(
        createConfirmation(
          () => resolve(true),
          () => resolve(false),
          {
            title: i18next.t('paymentIntegrations:tripos.printReceipt.title', {
              receiptRecipient: translatedReceiptRecipient,
            }),
            body: i18next.t('paymentIntegrations:tripos.printReceipt.body', {
              receiptRecipient: translatedReceiptRecipient,
            }),
          },
        ),
      ),
    );
  };
}

function checkIfShouldPrint(
  receiptRecipient: ValueOf<typeof CONSTANTS.RECEIPT_RECIPIENTS>,
) {
  return async (
    dispatch: ThunkDispatch<RootState, unknown, Action>,
    getState,
  ) => {
    const config = getCafaEntry2<object>(
      INTEGRATIONS.payment.tripos,
      INTEGRATION_TYPES.payment,
    )(getState());
    switch (config[receiptRecipient]) {
      case CONSTANTS.RECEIPT_PRINT_OPTIONS.ASK:
        return dispatch(confirmCopyPrint(receiptRecipient));
      case CONSTANTS.RECEIPT_PRINT_OPTIONS.NEVER:
        return false;
      case CONSTANTS.RECEIPT_PRINT_OPTIONS.ALWAYS:
      default:
        return true;
    }
  };
}

export function printReceipts(receipts: {
  cardholderPatchScript?: PatchScript;
  merchantPatchScript?: PatchScript;
}) {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action>) => {
    if (receipts.cardholderPatchScript) {
      const shouldPrint = await dispatch(
        checkIfShouldPrint(CONSTANTS.RECEIPT_RECIPIENTS.CARD_HOLDER),
      );
      if (shouldPrint) {
        await dispatch(printPatchscript(receipts.cardholderPatchScript));
      }
    }

    if (receipts.merchantPatchScript) {
      const shouldPrint = await dispatch(
        checkIfShouldPrint(CONSTANTS.RECEIPT_RECIPIENTS.MERCHANT),
      );
      if (shouldPrint) {
        await dispatch(printPatchscript(receipts.merchantPatchScript));
      }
    }
  };
}
